import { DateTime } from 'luxon';

import { useVoucherFormContext, VoucherFormState } from '../Context';
import InfoDisplay from '../../VoucherInfo/InfoDisplay';
import DiscountTypeFields from '../../VoucherInfo/Info/DiscountTypeFields';
import { RuleDisplayView } from '../../VoucherInfo/RuleDisplay';
import { RULE_GROUPS } from '../../VoucherInfo/Rules/ruleGroups';
import { DiscountType, DiscountTypeMap } from '../../../types/Voucher';
import { FreeAncillariesMap, FreeAncillariesRule } from '../../../types/Rules';
import { formatChannels } from '../../../helpers/allowedChannels';
import { getEntityLabel } from '../../../helpers/entityLabel';
import { FormMeta } from './readFormMeta';

type FormDiscountType = VoucherFormState['payload']['discountType'];

// The form keeps amounts flat (credit/discount + currency); the voucher display
// components expect the API shape with nested { amount, currency }.
export const toApiDiscountType = (discountType: FormDiscountType): DiscountType => {
  const { type, percentage, quantity, credit, discount } = discountType;
  const currency = discountType.currency || 'EUR';

  return {
    type,
    ...(percentage !== undefined && { percentage }),
    ...(quantity !== undefined && { quantity }),
    ...(credit !== undefined && { credit: { amount: credit, currency } }),
    ...(discount !== undefined && { discount: { amount: discount, currency } }),
  } as DiscountType;
};

const formatDateTime = (iso: string) => {
  const dt = DateTime.fromISO(iso, { setZone: true });
  return dt.isValid ? `${dt.toFormat('dd.MM.yyyy, HH:mm')} GMT${dt.toFormat('ZZ')}` : iso;
};

interface VoucherFormSummaryProps {
  meta: FormMeta;
}

const VoucherFormSummary = ({ meta }: VoucherFormSummaryProps) => {
  const {
    formState: { payload, comment },
  } = useVoucherFormContext();

  const { rules } = payload;
  const ruleTypes = new Set(rules.map(r => r.type));
  const ruleGroups = RULE_GROUPS.map(group => ({
    ...group,
    fields: group.fields.filter(f => ruleTypes.has(f)),
  })).filter(group => group.fields.length > 0);

  const ancillaries = rules
    .find((r): r is FreeAncillariesRule => r.type === 'free_ancillaries')
    ?.ancillaries.map(a => FreeAncillariesMap[a])
    .join(', ');

  const discountLabel =
    DiscountTypeMap[payload.discountType.type as DiscountType['type']] ?? payload.discountType.type;

  return (
    <div>
      <InfoDisplay
        layout='stacked'
        title='Voucher information'
        fields={[
          { label: 'Revenue type', value: meta.revenueType },
          { label: 'Voucher type', value: meta.voucherType },
          { label: 'Specification', value: payload.specification },
          { label: 'Reason / Issue', value: meta.reason },
        ]}
      />
      <InfoDisplay
        layout='stacked'
        title='Voucher structure'
        fields={
          payload.prefixOnly
            ? [
                { label: 'Campaign code', value: payload.prefix },
                {
                  label: 'Max usage count',
                  value: payload.maxUsageCount?.toString() ?? 'Unlimited',
                },
              ]
            : [
                { label: 'How many vouchers?', value: payload.count.toString() },
                { label: 'Prefix', value: payload.prefix },
                { label: 'Total length', value: payload.codeLength.toString() },
              ]
        }
      />
      <InfoDisplay
        layout='stacked'
        title='Discount'
        fields={[
          { label: 'Discount type', value: discountLabel },
          ...DiscountTypeFields(toApiDiscountType(payload.discountType)),
          { label: 'Added ancillaries', value: ancillaries },
          { label: 'Contract', value: payload.entity ? getEntityLabel(payload.entity) : null },
        ]}
      />
      <InfoDisplay
        layout='stacked'
        title='Booking'
        fields={[
          {
            label: 'Usage type',
            value: payload.multiUsage || payload.prefixOnly ? 'Multiple' : 'Single',
          },
          { label: 'Allowed platforms', value: formatChannels(rules) },
          { label: 'Voucher valid from', value: formatDateTime(payload.validSince) },
          { label: 'Voucher valid until', value: formatDateTime(payload.validUntil) },
        ]}
      />
      {ruleGroups.length > 0 && (
        <InfoDisplay layout='stacked' title='Rules' fields={[]}>
          {ruleGroups.map(group => (
            <RuleDisplayView
              key={group.title}
              layout='nested'
              title={group.title}
              fields={group.fields}
              rules={rules}
            />
          ))}
        </InfoDisplay>
      )}
      {comment && <InfoDisplay layout='stacked' title='Comment' fields={[{ value: comment }]} />}
    </div>
  );
};

export default VoucherFormSummary;
